import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { changeOrderApi } from '../api/endpoints.ts';
import { addOnsApi } from '../api/add-ons.ts';
import { Alert, Button, TextArea, TextInput } from './ui.tsx';
import { MoneyInput } from './money-input.tsx';
import { useSession } from '../state/session.tsx';
import { useLocalizedContent } from '../i18n/localized.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { formatShortDate } from '../i18n/datetime.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import type { ChangeOrder, ChangeOrderResult, ChangeOrderSendResult } from '@shared/types/index.ts';

/** What happened, for the screen that opened the form to say. */
export interface ExtraWorkDone {
  order: ChangeOrderResult;
  /**
   * The approval link when the customer was asked. It exists exactly once —
   * only its hash is stored — so this is the office's one chance to copy it.
   */
  link: { url: string; emailedTo: string | null } | null;
  /**
   * Set when the work was saved but the send request failed. That does not
   * prove nothing was sent (the link may have been minted before the error,
   * or the reply lost), so `order` is read again and the screen says to
   * check it rather than claiming it is still a draft.
   */
  sendError?: string;
}

/**
 * "Add extra work": the one form behind every place extra work starts — the
 * Extra work page, a visit, a customer's upcoming visits.
 *
 * Two ways to finish, because a customer asks for extra in two ways. Either
 * they already said yes (on the phone, at the door: "can you do the fridge
 * too?") and the work goes onto the visit now, or the office asks them first
 * and it goes on when they approve the link. Recording agreement changes a
 * visit's price there and then, so it is offered to people who can take
 * payments; anyone who may write a quote can still ask.
 *
 * On a visit, the amount is final, tax included, like every price line on a
 * visit. With no visit it rides the monthly invoice, which adds tax on top.
 */
export function ExtraWorkForm({
  customerId,
  appointmentId,
  currencyCode,
  onDone,
  onCancel,
}: {
  customerId: string;
  /** The visit it goes on; null bills on the monthly invoice. */
  appointmentId: string | null;
  /** The visit's currency, or the company's when there is no visit. */
  currencyCode: string;
  onDone: (done: ExtraWorkDone) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const { can, company, currentLocation } = useSession();
  const localized = useLocalizedContent();
  const canRecord = can('payments:write');
  const [title, setTitle] = useState('');
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [serviceDate, setServiceDate] = useState('');
  const [addOnId, setAddOnId] = useState<string | null>(null);
  /*
    On a visit the usual case is a customer who asked for it, so the default
    is "they already agreed". With no visit it is contract work going onto a
    monthly bill, which the client has always been asked to approve.
  */
  const [approval, setApproval] = useState<'agreed' | 'ask'>(appointmentId && canRecord ? 'agreed' : 'ask');
  const [agreedBy, setAgreedBy] = useState('');

  const addOns = useQuery({
    queryKey: ['visit-add-ons', company?.id, currentLocation?.id],
    queryFn: ({ signal }) => addOnsApi.list(false, signal),
    retry: false,
  });
  // Extra work only adds: a saved add-on with a negative amount (a discount
  // preset) belongs on the visit's price editor, not here. And a saved
  // add-on's amount is a final visit price, tax included, so none is offered
  // for the monthly invoice, which adds tax on top.
  const offered = appointmentId ? (addOns.data?.items ?? []).filter((addOn) => addOn.defaultAmountCents > 0) : [];

  /*
    One request id per submission, kept while that same submission is
    retried, as the visit price editor does: when the reply to "already
    agreed" is lost after the Worker added the work, trying again gets that
    order back instead of putting the work on the visit a second time.
  */
  const pending = useRef<{ fingerprint: string; requestId: string } | null>(null);

  const submit = useMutation({
    mutationFn: async (mode: 'agreed' | 'ask' | 'draft'): Promise<ExtraWorkDone> => {
      const body = {
        customerId,
        appointmentId,
        addOnId,
        title: title.trim(),
        description: description.trim() || null,
        amountCents: amountCents ?? 0,
        ...(appointmentId ? {} : { serviceDate: serviceDate || null }),
        ...(mode === 'agreed' ? { alreadyAgreed: true, agreedBy: agreedBy.trim() || null } : {}),
      };
      const fingerprint = JSON.stringify(body);
      if (pending.current?.fingerprint !== fingerprint)
        pending.current = { fingerprint, requestId: crypto.randomUUID() };
      const order = await changeOrderApi.create({ ...body, requestId: pending.current.requestId });
      pending.current = null;
      if (mode !== 'ask') return { order, link: null };
      // Saved already, so a failed send must not read as a failed save: that
      // invites a second click and a duplicate.
      try {
        const sent = await changeOrderApi.send(order.id);
        return { order: sent.changeOrder, link: { url: sent.approvalUrl, emailedTo: sent.emailedTo } };
      } catch (error) {
        const current = await changeOrderApi.get(order.id).catch(() => order);
        return { order: current, link: null, sendError: (error as Error).message };
      }
    },
    onSuccess: onDone,
  });

  const ready = title.trim().length > 0 && amountCents !== null && !submit.isPending;

  return (
    <div>
      {offered.length ? (
        <div className="mb-3">
          <p id="extra-work-add-ons" className="mb-2 text-sm text-[color:var(--color-muted)]">
            {t('changeOrders.form.savedAddOns')}
          </p>
          <div role="group" aria-labelledby="extra-work-add-ons" className="flex flex-wrap gap-2">
            {offered.map((addOn) => {
              const otherCurrency = addOn.currencyCode !== currencyCode;
              return (
                <Button
                  key={addOn.id}
                  variant="secondary"
                  aria-pressed={addOnId === addOn.id}
                  disabled={submit.isPending || otherCurrency}
                  title={
                    otherCurrency ? t('visitPricing.addOnOtherCurrency', { currency: addOn.currencyCode }) : undefined
                  }
                  onClick={() => {
                    if (addOnId === addOn.id) {
                      setAddOnId(null);
                      return;
                    }
                    setAddOnId(addOn.id);
                    setTitle(localized.text(addOn.name, addOn.nameTranslations));
                    setAmountCents(addOn.defaultAmountCents);
                  }}
                >
                  {t('visitPricing.addOnChip', {
                    name: localized.text(addOn.name, addOn.nameTranslations),
                    amount: formatCents(addOn.defaultAmountCents, addOn.currencyCode, getDateLocale()),
                  })}
                </Button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="grid gap-x-4 sm:grid-cols-2">
        <TextInput
          label={t('changeOrders.workTitle')}
          value={title}
          maxLength={160}
          disabled={submit.isPending}
          onChange={(event) => {
            setTitle(event.target.value);
            // Renamed or repriced it is still that add-on, as on the visit's
            // own price lines; cleared, it is nothing yet.
            if (!event.target.value.trim()) setAddOnId(null);
          }}
        />
        <MoneyInput
          currencyCode={currencyCode}
          label={appointmentId ? t('changeOrders.form.amountOnVisit') : t('changeOrders.amount')}
          valueCents={amountCents}
          min={0}
          disabled={submit.isPending}
          onChangeCents={setAmountCents}
        />
        {appointmentId ? null : (
          <TextInput
            label={t('changeOrders.serviceDate')}
            type="date"
            value={serviceDate}
            disabled={submit.isPending}
            onChange={(event) => setServiceDate(event.target.value)}
          />
        )}
      </div>
      <TextArea
        label={t('changeOrders.detail')}
        rows={2}
        value={description}
        disabled={submit.isPending}
        onChange={(event) => setDescription(event.target.value)}
      />

      <fieldset className="mt-3 space-y-2">
        <legend className="font-semibold">{t('changeOrders.form.approvalLegend')}</legend>
        {canRecord ? (
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="extra-work-approval"
              className="mt-1"
              checked={approval === 'agreed'}
              disabled={submit.isPending}
              onChange={() => setApproval('agreed')}
            />
            <span>
              {t('changeOrders.form.agreed')}
              <span className="block text-sm text-[color:var(--color-muted)]">
                {appointmentId ? t('changeOrders.form.agreedHintVisit') : t('changeOrders.form.agreedHintMonthly')}
              </span>
            </span>
          </label>
        ) : null}
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name="extra-work-approval"
            className="mt-1"
            checked={approval === 'ask'}
            disabled={submit.isPending}
            onChange={() => setApproval('ask')}
          />
          <span>
            {t('changeOrders.form.ask')}
            <span className="block text-sm text-[color:var(--color-muted)]">
              {appointmentId ? t('changeOrders.form.askHintVisit') : t('changeOrders.form.askHintMonthly')}
            </span>
          </span>
        </label>
      </fieldset>

      {approval === 'agreed' && canRecord ? (
        <TextInput
          label={t('changeOrders.form.agreedBy')}
          value={agreedBy}
          maxLength={160}
          disabled={submit.isPending}
          onChange={(event) => setAgreedBy(event.target.value)}
        />
      ) : null}

      {submit.isError ? (
        <Alert tone="error" title={t('changeOrders.createErrorTitle')}>
          {(submit.error as Error).message}
        </Alert>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        {approval === 'agreed' && canRecord ? (
          <Button variant="primary" busy={submit.isPending} disabled={!ready} onClick={() => submit.mutate('agreed')}>
            {appointmentId ? t('changeOrders.form.addToVisit') : t('changeOrders.form.addNow')}
          </Button>
        ) : (
          <>
            <Button variant="primary" busy={submit.isPending} disabled={!ready} onClick={() => submit.mutate('ask')}>
              {t('changeOrders.form.sendForApproval')}
            </Button>
            <Button variant="secondary" disabled={!ready} onClick={() => submit.mutate('draft')}>
              {t('changeOrders.saveDraft')}
            </Button>
          </>
        )}
        {onCancel ? (
          <Button variant="secondary" disabled={submit.isPending} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * What the form did, said once where it was opened: the approval link to copy,
 * the visit it went onto and what the crew's checklist got, or that it is a
 * draft because sending failed.
 */
export function ExtraWorkDoneNotice({ done }: { done: ExtraWorkDone }) {
  const { t } = useTranslation();
  const { order, link, sendError } = done;
  const tasks = order.checklistTasks;
  if (link) {
    return (
      <Alert tone="success" title={t('changeOrders.linkReadyTitle', { number: order.number })}>
        <p>
          {link.emailedTo
            ? t('changeOrders.done.emailedTo', { email: link.emailedTo })
            : t('changeOrders.done.notEmailed')}{' '}
          {t('changeOrders.linkReadyBody')}
        </p>
        <p className="mt-2 break-all font-mono text-sm">{link.url}</p>
      </Alert>
    );
  }
  if (sendError) {
    return (
      <Alert tone="warning" title={t('changeOrders.done.sendFailedTitle')}>
        <p>{sendError}</p>
        <p className="mt-1">
          {t('changeOrders.done.sendFailedBody', { status: t(`changeOrders.status.${order.status}`) })}
        </p>
      </Alert>
    );
  }
  if (order.status === 'draft') {
    return (
      <Alert tone="info" title={t('changeOrders.done.draftTitle')}>
        <p>{t('changeOrders.done.draftBody')}</p>
      </Alert>
    );
  }
  // Approved, but its visit could not take it (paid or closed meanwhile): not
  // a success, and the office has to place it.
  if (order.appointmentId && !order.visitAppliedAt) {
    return (
      <Alert tone="warning" title={t('changeOrders.done.notAddedTitle')}>
        <p>
          {t('changeOrders.notOnVisit', {
            reason: t(`changeOrders.visitBlocked.${order.visitBlockedReason ?? 'closed'}`),
          })}
        </p>
      </Alert>
    );
  }
  return (
    <Alert tone="success" title={t('changeOrders.done.addedTitle')}>
      <p>
        {order.visit
          ? t('changeOrders.done.addedToVisit', {
              title: order.title,
              date: formatShortDate(order.visit.scheduledDate),
            })
          : t('changeOrders.done.addedMonthly', { title: order.title })}
      </p>
      {tasks?.added.length ? (
        <p className="mt-1">{t('changeOrders.done.tasksAdded', { tasks: tasks.added.join(', ') })}</p>
      ) : null}
      {tasks?.skipped.length ? (
        <p className="mt-1">{t('changeOrders.done.tasksSkipped', { tasks: tasks.skipped.join(', ') })}</p>
      ) : null}
    </Alert>
  );
}

/**
 * Who agreed and where the work landed. On a competitive account the first
 * line is the one that settles an argument six months later; the second is
 * the one the office acts on when approved work could not reach its visit.
 */
export function ExtraWorkOrderState({ order }: { order: ChangeOrder }) {
  const { t } = useTranslation();
  const muted = 'text-sm text-[color:var(--color-muted)]';
  return (
    <>
      {order.status === 'approved' && order.recordedByOffice ? (
        <p className={muted}>
          {order.approvedByName
            ? t('changeOrders.recordedByOfficeNamed', { name: order.approvedByName })
            : t('changeOrders.recordedByOffice')}
        </p>
      ) : order.approvedByName ? (
        <p className={muted}>{t('changeOrders.approvedBy', { name: order.approvedByName })}</p>
      ) : null}
      {/*
        The checklist is claimed only when the task is known to be on it:
        hand-typed work records that, while work from a saved add-on leaves
        it to the add-on's own task and says just "on the visit".
      */}
      {order.visitAppliedAt ? (
        <p className={muted}>
          {order.checklistTaskOutcome === 'added'
            ? t('changeOrders.onVisit')
            : order.checklistTaskOutcome === 'skipped'
              ? t('changeOrders.onVisitChecklistSkipped')
              : t('changeOrders.onVisitOnly')}
        </p>
      ) : null}
      {order.status === 'approved' && order.visitBlockedReason && !order.visitAppliedAt && !order.monthlyInvoiceId ? (
        <p className="text-sm text-[color:var(--color-danger)]">
          {t('changeOrders.notOnVisit', { reason: t(`changeOrders.visitBlocked.${order.visitBlockedReason}`) })}
        </p>
      ) : null}
      {order.monthlyInvoiceId ? <p className={muted}>{t('changeOrders.billed')}</p> : null}
    </>
  );
}

/**
 * The next step for one piece of extra work, wherever it is listed: send it
 * (again), record that the customer agreed, try again to put approved work on
 * its visit, or withdraw it. Approved work has no withdraw: the customer
 * agreed to buy it, and unsaying that is a credit, not a button.
 */
export function ExtraWorkActions({
  order,
  onChanged,
  onSent,
  onAgreed,
}: {
  order: ChangeOrder;
  onChanged: () => void;
  /** The approval link, which exists exactly once. */
  onSent: (result: ChangeOrderSendResult) => void;
  /** What recording agreement, or trying the visit again, came to. */
  onAgreed?: (order: ChangeOrderResult) => void;
}) {
  const { t } = useTranslation();
  const { can } = useSession();
  const send = useMutation({
    mutationFn: () => changeOrderApi.send(order.id),
    onSuccess: (result) => {
      onSent(result);
      onChanged();
    },
    // A failed send may still have gone through (the link minted before the
    // email failed, or the reply lost), so the list is read again rather
    // than left showing the state from before.
    onError: onChanged,
  });
  const agree = useMutation({
    mutationFn: () => changeOrderApi.agree(order.id),
    onSuccess: (result) => {
      onAgreed?.(result);
      onChanged();
    },
    // Likewise a failed answer may have been approved and put on the visit.
    onError: onChanged,
  });
  // A retry says what it came to like agreeing does: on the visit or still
  // not, and whether its checklist task went on or was skipped.
  const addToVisit = useMutation({
    mutationFn: () => changeOrderApi.addToVisit(order.id),
    onSuccess: (result) => {
      onAgreed?.(result);
      onChanged();
    },
    onError: onChanged,
  });
  const cancel = useMutation({ mutationFn: () => changeOrderApi.cancel(order.id), onSuccess: onChanged });
  const busy = send.isPending || agree.isPending || addToVisit.isPending || cancel.isPending;
  const pending = order.status === 'draft' || order.status === 'sent';
  const failure = send.isError
    ? { title: t('changeOrders.sendErrorTitle'), error: send.error }
    : agree.isError
      ? { title: t('changeOrders.agreeErrorTitle'), error: agree.error }
      : addToVisit.isError
        ? { title: t('changeOrders.addToVisitErrorTitle'), error: addToVisit.error }
        : cancel.isError
          ? { title: t('changeOrders.cancelErrorTitle'), error: cancel.error }
          : null;

  return (
    <div className="flex flex-wrap gap-2">
      {pending && can('quotes:write') ? (
        <Button onClick={() => send.mutate()} busy={send.isPending} disabled={busy && !send.isPending}>
          {order.status === 'sent' ? t('changeOrders.resend') : t('changeOrders.send')}
        </Button>
      ) : null}
      {/*
        The customer said yes some other way — on the phone, by text.
        Recording it approves the work, and on a visit changes that visit's
        price, so it is for people who can take payments.
      */}
      {pending && can('payments:write') ? (
        <Button onClick={() => agree.mutate()} busy={agree.isPending} disabled={busy && !agree.isPending}>
          {t('changeOrders.agree')}
        </Button>
      ) : null}
      {order.status === 'approved' &&
      order.appointmentId &&
      !order.visitAppliedAt &&
      !order.monthlyInvoiceId &&
      can('payments:write') ? (
        <Button
          onClick={() => addToVisit.mutate()}
          busy={addToVisit.isPending}
          disabled={busy && !addToVisit.isPending}
        >
          {t('changeOrders.tryAgain')}
        </Button>
      ) : null}
      {order.status !== 'approved' && order.status !== 'cancelled' && can('quotes:write') ? (
        <Button onClick={() => cancel.mutate()} busy={cancel.isPending} disabled={busy && !cancel.isPending}>
          {t('changeOrders.cancel')}
        </Button>
      ) : null}
      {failure ? (
        <div className="basis-full">
          <Alert tone="error" title={failure.title}>
            {failure.error.message}
          </Alert>
        </div>
      ) : null}
    </div>
  );
}
